/**
 * The town somebody else built.
 *
 * *"in the siege, there was nothing in the enemy town but few walls no
 * buildings"* — and that was exactly true. A fortified province handed to
 * battle mode got `buildFieldWorks`: forty-five tiles of hedge with two gaps in
 * it, no roofs, no streets, nothing to take. Taking a capital was walking at a
 * fence.
 *
 * This raises a real one. Three rules decide everything below.
 *
 * **1. It is built out of the player's own catalogue.** Every roof here is a
 * `BUILDING_TYPES` entry placed through `placeBuilding`, exactly as the player
 * places theirs; every wall goes down through `setWall`. There is deliberately
 * no second vocabulary of "enemy buildings" — one would drift from the real one
 * within a release, and the renderer, the targeting pass, the flow field and
 * the rubble layer would all have to learn about it. The AI builds a Barracks
 * because a Barracks is what a town of this people at this Age has.
 *
 * **2. What is in it is decided by the holding, not by the scenario.** A
 * Hamlet at tier 1 gets a palisade ring, a hall and a dozen roofs. A Seat of the
 * Realm at tier 10 gets a bastion circuit with towers on it, a market, a drill
 * yard, a smeltery and sixty buildings. The Age is read off the rung — a Great
 * City builds like a fifth-Age town because that is what a Great City is — so
 * the strategic layer's one number for "how much has been spent on this place"
 * decides what you have to fight through, without needing a second one.
 *
 * **3. It is laid out along streets, because streets are what the rest of the
 * simulation already understands.** Roads go down first, from every gate to the
 * civic square, with clear routes around the existing Hearth. Plots are then refused any tile a
 * road is on and *preferred* where they touch one — which is `touchesRoad` in
 * `districts.ts`, the same rule that pays the player's own producers. The
 * result is a town with a shape rather than a solid block of sheds, and it is a
 * shape the pathfinder can get through: a besieger's route down a street is
 * cheap, and it is the streets that make the difference between a company
 * getting into the middle of a town and one grinding through its outskirts.
 *
 * Nothing here reads a clock or `Math.random`; every roll comes off the `Rng`
 * the caller hands in, so the same battle seed builds the same town.
 */

import { BUILDING_TYPES, buildingTypeIndex, GATE_DEF, TOWER_DEF, wallTierDef } from '../../data/buildings.ts';
import { Terrain, WallKind } from '../types.ts';
import { placeBuilding, setWall, type World } from '../world.ts';
import { nextFloat, nextInt, type Rng } from '../../core/rng.ts';
import { clamp } from '../../core/math.ts';
import { VILLAGE_RUNG } from '../campaign/commonwealth.ts';
import { clearNodesInRect } from './nodes.ts';
import { planTownCircuit, townTowerPlots, townInterior, townStreet, type TownBounds, type TownPoint } from './towncircuit.ts';

/** What the strategic layer knows about a place, and all this needs from it. */
export interface TownPlan {
  cx: number;
  cy: number;
  /** Whose town it is. Its walls should be `World.wallTeam`. */
  team: number;
  /** The holding's rung, 1–10. See `TIER_NAMES` in `campaign/holdings.ts`. */
  tier: number;
  /** The province's fortification level, 0–3. Decides the wall and the towers. */
  fort: number;
  /** A capital is a rung and a half grander than its tier alone says. */
  capital: boolean;
  /** Which people built it: gates race-locked buildings. `World.raceId`. */
  raceId: string;
}

/** What was actually raised. Returned so a harness can assert on it. */
export interface TownReport {
  buildings: number;
  wallTiles: number;
  gates: number;
  towers: number;
  radius: number;
  age: number;
  centre: TownPoint;
  bounds: TownBounds;
  /** Enclosed ground, excluding curtain cells; indexed by map tile. */
  interior: Uint8Array;
}

/**
 * The Age a holding builds at.
 *
 * Two rungs to an Age, and a capital counts as one rung grander. Deliberately
 * derived rather than stored: the campaign has no Age of its own, a nation's
 * technology is not modelled province by province, and inventing a second
 * number for "how advanced is this place" would immediately disagree with the
 * first one. What a player reads off it is legible — a Market Town is
 * second-Age, a Great City is fifth — which is the property that matters.
 */
export function townAge(tier: number, capital: boolean): number {
  return clamp(1 + Math.floor((tier - 1 + (capital ? 1 : 0)) / 2), 1, 5);
}

/** How far the wall stands from the middle, in tiles. */
export function townRadius(tier: number, capital: boolean): number {
  return Math.round(clamp(9 + tier * 1.35 + (capital ? 2 : 0), 10, 26));
}

/**
 * The programme, in the order it is laid out from the middle outward.
 *
 * A list of ids and how many of each, because that is the whole of what "a
 * town of this people at this Age looks like" is. Order is placement order and
 * placement order is geography: the seat and the things that answer to it in
 * the middle, the trades and the stores round them, dwellings filling out to
 * the wall, and the fighting platforms last so they end up on the rampart.
 *
 * Ids that this people may not raise, or that the Age has not reached, fall out
 * in `pickProgramme` — so a Hamlet's list is a handful of huts and a hall and
 * the same code produced it.
 */
interface Want {
  id: string;
  /** Base count at tier 1, and how much each further rung adds. */
  base: number;
  per: number;
}

const SEATS = ['hollow_crown', 'gilded_hall', 'war_room', 'academy', 'tavern'];

const PROGRAMME: readonly Want[] = [
  // The middle: what a town is administered from.
  { id: 'chapel', base: 1, per: 0.1 },
  { id: 'tavern', base: 1, per: 0.1 },
  { id: 'market', base: 0, per: 0.14 },
  { id: 'war_room', base: 0, per: 0.12 },
  { id: 'academy', base: 0, per: 0.12 },
  // The garrison quarter.
  { id: 'barracks', base: 1, per: 0.2 },
  { id: 'archery_range', base: 0, per: 0.16 },
  { id: 'armoury', base: 0, per: 0.14 },
  { id: 'stable', base: 0, per: 0.12 },
  { id: 'drill_yard', base: 0, per: 0.1 },
  { id: 'siege_yard', base: 0, per: 0.08 },
  { id: 'infirmary', base: 0, per: 0.1 },
  // The trades. Nothing that has to stand on a particular kind of ground —
  // those are the hinterland's, below, and a quarry inside a town is a lie.
  { id: 'storehouse', base: 1, per: 0.16 },
  { id: 'granary', base: 1, per: 0.14 },
  { id: 'bakery', base: 1, per: 0.14 },
  { id: 'sawmill', base: 0, per: 0.16 },
  { id: 'mill', base: 0, per: 0.12 },
  { id: 'workshop', base: 0, per: 0.12 },
  { id: 'smelter', base: 0, per: 0.12 },
  { id: 'counting_house', base: 0, per: 0.1 },
  { id: 'smeltery', base: 0, per: 0.08 },
  { id: 'great_forge', base: 0, per: 0.06 },
];

/**
 * Where the people live: the bulk of any town, and the reason a taken capital
 * reads as a place rather than as a compound.
 *
 * A list of its own, and laid **last of everything inside the wall**, because
 * dwellings are what a town has as much of as it has room for. Left in the
 * programme above they simply ate every remaining plot, and a town with thirty
 * houses and no Grudge Hall in it is a town with no people in it.
 */
const DWELLINGS: readonly Want[] = [
  { id: 'house', base: 5, per: 2.1 },
  { id: 'longhouse', base: 0, per: 0.5 },
];

/** Raised on outer plots reserved before housing fills the bailey. */
const RAMPART: readonly Want[] = [
  { id: 'arrow_tower', base: 1, per: 0.22 },
  { id: 'ballista_tower', base: 0, per: 0.16 },
  { id: 'watchtower', base: 1, per: 0.18 },
];

/** Food plots protected inside the outer bailey. */
const HINTERLAND: readonly Want[] = [{ id: 'farm', base: 2, per: 0.4 }];

/**
 * What a *village* is made of, which is not a smaller version of the above.
 *
 * > *"villages, not cities, should have their own set of buildings separate to
 * > the city — they should be bonus material for the city, not full cities
 * > themselves"*
 *
 * The old generator ran one programme at every rung and let `pickProgramme`'s
 * arithmetic thin it out, so a hamlet came out as a city with one of everything:
 * a barracks, a war room, an academy and a counting house, at rung two, in a
 * place with eleven roofs. That is the "full cities themselves" being
 * complained about, and no amount of tuning `per` fixes it — a town with one
 * academy in it is still a town with an academy in it.
 *
 * So a village has its own list, and it is deliberately **all ground and
 * roofs**: the fields, the woodlot, the quarry, the seam, somewhere to put what
 * they pull out of them, and a chapel. It is the same list a player is allowed
 * to raise in one — `BuildingType.village`, and `tests/towns.test.ts` holds the
 * two in step, because a generator that builds what the player may not is a
 * village the player cannot repair.
 *
 * The counts are not thin versions of the town's either. A village that is
 * mostly farm is *the point*: it is what the city eats.
 */
const VILLAGE_PROGRAMME: readonly Want[] = [
  { id: 'granary', base: 1, per: 0.2 },
  { id: 'storehouse', base: 1, per: 0.2 },
  { id: 'chapel', base: 1, per: 0 },
  { id: 'woodcutter', base: 1, per: 0.3 },
  { id: 'quarry', base: 1, per: 0.25 },
  { id: 'iron_mine', base: 0, per: 0.25 },
  { id: 'bakery', base: 1, per: 0.2 },
  { id: 'sawmill', base: 0, per: 0.25 },
  { id: 'mill', base: 0, per: 0.25 },
];

/** A village's roofs. Fewer than a town's, and no longhouses. */
const VILLAGE_DWELLINGS: readonly Want[] = [{ id: 'house', base: 4, per: 1.4 }];

/**
 * A village's defences: a watchtower and some sharpened wood.
 *
 * Not `RAMPART`, which is arrow towers and ballistae — the things a garrison
 * mans. A village has no garrison. What it has is somewhere to stand and shout
 * from, which is the difference between a place that sees the raiders coming
 * and one that does not.
 */
const VILLAGE_RAMPART: readonly Want[] = [
  { id: 'watchtower', base: 1, per: 0.12 },
  { id: 'caltrop_field', base: 0, per: 0.2 },
];

/** A village's fields — more of them than a town's, because that is its job. */
const VILLAGE_HINTERLAND: readonly Want[] = [
  { id: 'farm', base: 3, per: 0.9 },
  { id: 'hunters_lodge', base: 1, per: 0.2 },
];

/** Can this people, at this Age, raise this at all? */
function allowed(idx: number, age: number, raceId: string): boolean {
  const def = BUILDING_TYPES[idx];
  if (def.category === 'core') return false;
  if (def.tier > age) return false;
  if (def.race !== '' && def.race !== raceId) return false;
  return true;
}

/** The programme, filtered to what this town could actually have built. */
function pickProgramme(
  wants: readonly Want[],
  tier: number,
  age: number,
  raceId: string,
): Array<{ idx: number; n: number }> {
  const out: Array<{ idx: number; n: number }> = [];
  for (const want of wants) {
    const idx = buildingTypeIndex(want.id);
    if (idx < 0 || !allowed(idx, age, raceId)) continue;
    const n = Math.floor(want.base + want.per * (tier - 1) * 2);
    if (n <= 0) continue;
    out.push({ idx, n });
  }
  return out;
}

/**
 * Water a town must not be built on, on, or over.
 *
 * `Marsh` is not in here and that is deliberate: a marsh is bad ground, not
 * water, and `plotFree` already refuses to put a roof on one. What this decides
 * is where a *wall* may go and where the middle of a town may sit, and a wall
 * across a bog is a real wall.
 */
function isWater(w: World, x: number, y: number): boolean {
  if (!w.map.inBounds(x, y)) return false;
  const t = w.map.terrain[w.map.idx(x, y)];
  return t === Terrain.River || t === Terrain.Deep;
}

/**
 * How wet the ground under a town of this radius is.
 *
 * Counts the channel tiles inside the circuit — not the ones under the wall,
 * the ones the town would be *standing on*. Used only to choose between
 * candidate centres, so the units do not matter as long as they are comparable.
 */
function wetness(w: World, cx: number, cy: number, r: number): number {
  let n = 0;
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      if ((x - cx) ** 2 + (y - cy) ** 2 > r * r) continue;
      if (isWater(w, x, y)) n++;
    }
  }
  return n;
}

/**
 * Put the middle of the town on dry land, and prefer to keep the river outside
 * the wall rather than through the middle of it.
 *
 * > *"the way you generate basic villages needs updating — it tried building
 * > over a river, for example. It should work around it."*
 *
 * The generator was handed a point and built a ring on it, and nothing between
 * the point and the ring ever looked at the ground: a hold whose map put a
 * channel through the middle got a wall laid straight across the water, gates
 * in the river, and spoke roads that stopped dead at both banks. That is not a
 * town that has a river in it; it is a town that has not noticed one.
 *
 * The answer is not to move the town off the water altogether — people build
 * *at* rivers, that is what rivers are for, and a village shoved half a map
 * inland to avoid a stream is a worse lie than the one being fixed. So this
 * searches a short spiral of candidate centres and takes the driest one within
 * a few tiles, which in practice slides the ring to one bank and leaves the
 * water running past the wall instead of through the hall. Where the whole
 * neighbourhood is wet it gives up and returns the best it found, and the
 * circuit below fits a complete dry enclosure on that bank.
 *
 * Deterministic — a fixed scan order, no `Rng` draw — so the same seed still
 * builds the same town, which is the promise the whole of this file is written
 * around.
 */
const CENTRE_NUDGE = 6;

function drySite(w: World, cx: number, cy: number, r: number): { x: number; y: number } {
  let bestX = cx;
  let bestY = cy;
  let best = Infinity;
  for (let d = 0; d <= CENTRE_NUDGE; d++) {
    for (let dy = -d; dy <= d; dy++) {
      for (let dx = -d; dx <= d; dx++) {
        // The shell of the square, so the search really does widen outward and
        // the first acceptable answer is the nearest one.
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== d) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (!w.map.inBounds(x - r - 1, y - r - 1) || !w.map.inBounds(x + r + 1, y + r + 1)) continue;
        // The Hearth's own tile is not negotiable: a hall in a river is the
        // picture the owner is describing.
        if (isWater(w, x, y)) continue;
        const wet = wetness(w, x, y, r);
        if (wet < best) {
          best = wet;
          bestX = x;
          bestY = y;
        }
        // Dry ground a tile from where we were asked to build beats drier
        // ground six tiles away every time.
        if (wet === 0) return { x, y };
      }
    }
  }
  return { x: bestX, y: bestY };
}

/** Is every tile of this footprint free for a roof? */
function plotFree(w: World, tx: number, ty: number, bw: number, bh: number, roads: Uint8Array, interior: Uint8Array): boolean {
  const map = w.map;
  for (let y = ty - 1; y <= ty + bh; y++) {
    for (let x = tx - 1; x <= tx + bw; x++) {
      if (!map.inBounds(x, y)) return false;
      const k = map.idx(x, y);
      // A one-tile skirt of clearance, so the town has gaps a man can walk
      // down and does not fuse into one solid block — which is the same fault
      // as a wall with no gate, one layer in.
      if (map.building[k] >= 0) return false;
      const inside = x >= tx && x < tx + bw && y >= ty && y < ty + bh;
      if (!inside) continue;
      if (!interior[k] || map.wall[k] !== WallKind.None) return false;
      if (roads[k] !== 0) return false;
      const t = map.terrain[k];
      if (t === Terrain.River || t === Terrain.Deep || t === Terrain.Bridge || t === Terrain.Marsh) return false;
    }
  }
  return true;
}

/** Does this footprint have a street on it? */
function onAStreet(w: World, tx: number, ty: number, bw: number, bh: number, roads: Uint8Array): boolean {
  const map = w.map;
  for (let y = ty - 1; y <= ty + bh; y++) {
    for (let x = tx - 1; x <= tx + bw; x++) {
      if (!map.inBounds(x, y)) continue;
      if (roads[map.idx(x, y)] !== 0) return true;
    }
  }
  return false;
}

/**
 * Raise a town, walls and all. Returns what was actually built.
 *
 * Every roll comes off `rng`, and the order of the rolls is fixed by the order
 * of the loops, so the same seed and the same plan give the same town on every
 * machine and in every replay.
 */
export function raiseTown(w: World, rng: Rng, plan: TownPlan): TownReport {
  const map = w.map;
  const age = townAge(plan.tier, plan.capital);
  const R = townRadius(plan.tier, plan.capital);
  // Existing city cores are immovable. Fit the curtain around the real Hearth,
  // rather than sliding the town and leaving its original centre outside.
  const hearth = w.hearth >= 0 && w.buildings.isAlive(w.hearth) ? w.hearth : -1;
  const protectedHearth = hearth >= 0 && Math.hypot(w.buildings.tx[hearth]-plan.cx,w.buildings.ty[hearth]-plan.cy)<R;
  const site = protectedHearth ? {x:Math.round(plan.cx),y:Math.round(plan.cy)} : drySite(w,Math.round(plan.cx),Math.round(plan.cy),R);
  let core: TownBounds = {x0:site.x-1,y0:site.y-1,x1:site.x+1,y1:site.y+1};
  if (protectedHearth) {
    const def = BUILDING_TYPES[w.buildings.typeId[hearth]], x=w.buildings.tx[hearth],y=w.buildings.ty[hearth];
    core={x0:x-1,y0:y-1,x1:x+def.w,y1:y+def.h};
  }
  const circuit=planTownCircuit(w,rng,site.x,site.y,R,core);
  const {x:cx,y:cy}=circuit.centre;
  const village = !plan.capital && plan.tier < VILLAGE_RUNG;
  const report: TownReport = { buildings:0,wallTiles:0,gates:0,towers:0,radius:R,age,
    centre:circuit.centre,bounds:circuit.bounds,interior:circuit.interior };
  const tierDef=clamp(1+plan.fort,1,3),wallHp=wallTierDef(tierDef).hp;
  for (const p of circuit.tiles) setWall(w,p.x,p.y,WallKind.Wall,wallHp,tierDef);
  for (const gate of circuit.gates) for(const p of gate.tiles) setWall(w,p.x,p.y,WallKind.Gate,GATE_DEF.hp,tierDef);
  if(plan.fort>=2) for(const p of townTowerPlots(w,circuit,plan.fort>=3?6:4)) {
    for(let dy=0;dy<2;dy++) for(let dx=0;dx<2;dx++) setWall(w,p.x+dx,p.y+dy,WallKind.Tower,TOWER_DEF.hp,tierDef);
    report.towers++;
  }
  report.interior=townInterior(w,circuit.bounds,circuit.centre);
  for(const p of circuit.tiles) {
    const kind=map.wall[map.idx(p.x,p.y)];
    if(kind===WallKind.Wall) report.wallTiles++;
    else if(kind===WallKind.Gate) report.gates++;
  }

  // Streets connect each gate to a civic junction beside the actual core.
  // They reserve walkable lanes before plots are laid out, including all three
  // tiles through a gate throat. Water remains outside this dry bailey.
  const roads=new Uint8Array(map.terrain.length);
  const layRoad=(x:number,y:number):void=>{
    if(!map.inBounds(x,y))return;const k=map.idx(x,y),t=map.terrain[k];
    if(map.wall[k]!==WallKind.None||map.building[k]>=0||t===Terrain.River||t===Terrain.Deep||t===Terrain.Bridge)return;
    clearNodesInRect(w.nodes,x,y,x,y);map.terrain[k]=Terrain.Road;roads[k]=1;
  };
  let junction={x:cx,y:cy},distance=Infinity;
  for(let y=circuit.bounds.y0+1;y<circuit.bounds.y1;y++)for(let x=circuit.bounds.x0+1;x<circuit.bounds.x1;x++){
    const k=map.idx(x,y);if(!report.interior[k]||map.building[k]>=0)continue;
    const d=Math.abs(x-cx)+Math.abs(y-(core.y1+2));if(d<distance){distance=d;junction={x,y};}
  }
  for(const gate of circuit.gates) {
    for(const p of gate.tiles) {
      for(let depth=1;depth<=3;depth++) layRoad(p.x+gate.dx*depth,p.y+gate.dy*depth);
      const from={x:p.x-gate.dx,y:p.y-gate.dy};
      for(const k of townStreet(w,report.interior,from,junction)) layRoad(k%map.w,Math.floor(k/map.w));
    }
  }
  map.terrainVersion++;

  /**
   * The plots, from the middle outward.
   *
   * Concentric rings of candidate corners with a per-ring phase off the `Rng`,
   * so a town is not four identical quadrants and two towns of the same tier
   * are not the same town. Sorted by radius, which is what makes the
   * programme's order into the town's geography.
   */
  const plots: Array<{ x: number; y: number; r: number }> = [];
  for (let r = 3; r <= R - 1.5; r += 2.6) {
    const n = Math.max(5, Math.round((Math.PI * 2 * r) / 4.2));
    const phase = nextFloat(rng) * Math.PI * 2;
    for (let j = 0; j < n; j++) {
      const a = phase + (j / n) * Math.PI * 2;
      plots.push({ x: Math.round(cx + Math.cos(a) * r), y: Math.round(cy + Math.sin(a) * r), r });
    }
  }

  for(let y=circuit.bounds.y0+2;y<circuit.bounds.y1-1;y+=2)for(let x=circuit.bounds.x0+2;x<circuit.bounds.x1-1;x+=2){
    if(report.interior[map.idx(x,y)])plots.push({x,y,r:Math.hypot(x-cx,y-cy)});
  }

  const place = (idx: number, tx: number, ty: number): boolean => {
    const def = BUILDING_TYPES[idx];
    if (!plotFree(w, tx, ty, def.w, def.h, roads, report.interior)) return false;
    if (placeBuilding(w, idx, tx, ty, plan.team, true) < 0) return false;
    report.buildings++;
    return true;
  };

  // The seat: the grandest hall this people has reached, dead centre. A village
  // has no seat and never has — that is most of what the word means.
  for (const id of village ? [] : SEATS) {
    const idx = buildingTypeIndex(id);
    if (idx < 0 || !allowed(idx, age, plan.raceId)) continue;
    const def = BUILDING_TYPES[idx];
    if (place(idx, cx - (def.w >> 1), cy - (def.h >> 1))) break;
  }

  // Reserve complete defence footprints before housing consumes the outer
  // plots. Two-tile watchtowers cannot be squeezed into the leftover gaps.
  const rampart = plots.filter((p) => p.r >= R - 6);
  for (const want of pickProgramme(village ? VILLAGE_RAMPART : RAMPART, plan.tier, age, plan.raceId)) {
    const def = BUILDING_TYPES[want.idx];
    let made = 0;
    for (let scan = 0; scan < rampart.length && made < want.n; scan++) {
      const p = rampart[(scan * 7 + nextInt(rng, 0, 3)) % rampart.length];
      if (place(want.idx, p.x - (def.w >> 1), p.y - (def.h >> 1))) made++;
    }
    // The varied first pass may revisit candidates; exhaust every legal outer
    // site before giving up a requested tower.
    for (const p of rampart) {
      if (made >= want.n) break;
      if (place(want.idx, p.x - (def.w >> 1), p.y - (def.h >> 1))) made++;
    }
  }

  /**
   * The programme against the plots, in two passes over each want.
   *
   * The first pass will only take a plot that touches a street, which is what
   * puts the town on its roads rather than merely near them; the second takes
   * whatever is left, so a want is never silently dropped because the streets
   * were full. Two passes rather than a score, because a score here would be
   * `districts.ts` written a second time in a file that does not need it.
   */
  let cursor = 0;
  const runProgramme = (wants: Array<{ idx: number; n: number }>): void => {
    for (const want of wants) {
      const def = BUILDING_TYPES[want.idx];
      for (let made = 0; made < want.n; ) {
        let placed = false;
        for (let pass = 0; pass < 2 && !placed; pass++) {
          for (let scan = 0; scan < plots.length; scan++) {
            const p = plots[(cursor + scan) % plots.length];
            const tx = p.x - (def.w >> 1);
            const ty = p.y - (def.h >> 1);
            if (!plotFree(w, tx, ty, def.w, def.h, roads, report.interior)) continue;
            if (pass === 0 && !onAStreet(w, tx, ty, def.w, def.h, roads)) continue;
            if (!place(want.idx, tx, ty)) continue;
            cursor = (cursor + scan + 1) % plots.length;
            placed = true;
            break;
          }
        }
        if (!placed) break;
        made++;
      }
    }
  };

  /**
   * The programme, and which one.
   *
   * A village runs its own list and does not touch the town's — see
   * `VILLAGE_PROGRAMME` for why thinning the town's list was never going to be
   * the answer. The signature pass below is skipped for one too: a people's
   * Ossuary or Grudge Hall is the thing that makes a *city* theirs, and one
   * standing alone in a hamlet of nine roofs is the same category error as the
   * academy was.
   */
  if(village) {
    runProgramme(pickProgramme(VILLAGE_HINTERLAND, plan.tier, age, plan.raceId));
    runProgramme(pickProgramme(VILLAGE_DWELLINGS, plan.tier, age, plan.raceId));
  }
  runProgramme(pickProgramme(village ? VILLAGE_PROGRAMME : PROGRAMME, plan.tier, age, plan.raceId));

  /**
   * The buildings only this people has, found in the catalogue rather than
   * listed here.
   *
   * `BuildingType.race` already says "only the Karrun raise a Grudge Hall", and
   * that field is the answer to *"is this two peoples or one town in two
   * tints"* — the question `src/tools/townsheet.ts` exists to ask of the art.
   * Reading it means a people added to `data/races.ts` tomorrow builds its own
   * town the day it lands, with nothing written here; a table of ids per race
   * would have to be remembered, and would not be.
   *
   * One of each, and a second of each past the seventh rung: these are the
   * signature buildings, and a town with four Ossuaries in it is not a town
   * with a character, it is a town with a bug.
   */
  const signature: Array<{ idx: number; n: number }> = [];
  if (!village) {
    for (let i = 0; i < BUILDING_TYPES.length; i++) {
      const def = BUILDING_TYPES[i];
      if (def.race !== plan.raceId) continue;
      if (!allowed(i, age, plan.raceId)) continue;
      signature.push({ idx: i, n: plan.tier >= 7 ? 2 : 1 });
    }
  } else {
    /**
     * A village still gets the one building that says whose village it is —
     * but only if it is a building a village may have at all.
     *
     * Which for nine of the eleven peoples is their economy building: the
     * Verdant's Heartgrove, the Undertow's Sump Market, the Cogwarren's Salvage
     * Yard. That is exactly right — what a people's *hamlets* look like is
     * their ground and what they pull out of it, not their halls. Where a
     * people has no such building, they get none, and their villages are plain,
     * which is a truthful answer rather than a missing one.
     */
    for (let i = 0; i < BUILDING_TYPES.length; i++) {
      const def = BUILDING_TYPES[i];
      if (def.race !== plan.raceId || !def.village) continue;
      if (!allowed(i, age, plan.raceId)) continue;
      signature.push({ idx: i, n: 1 });
    }
  }
  runProgramme(signature);
  // Food plots belong to the defended bailey too, before housing fills it.
  if(!village) {
    runProgramme(pickProgramme(HINTERLAND, plan.tier, age, plan.raceId));
    runProgramme(pickProgramme(DWELLINGS, plan.tier, age, plan.raceId));
  }

  w.dirty.flow = true;
  w.dirty.districts = true;
  return report;
}
